import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { JourneySafetyState } from '@aztec-realms/shared-types';
import type { DiscoveryMarkerView } from '@aztec-realms/shared-types';

import {
  cancelForegroundJourney,
  finishForegroundJourney,
  persistedJourneyId,
  startForegroundJourney,
  submitForegroundJourneySamples,
  type JourneyAuthoritySnapshot,
} from './data/journey-api';
import { demoModeEnabled, visualFixtureName } from './data/vertical-slice';
import type { JourneyMapPosition } from './explore/codex-map';
import {
  appendJourneyPresentationPosition,
  createForegroundJourneyTracker,
  mapPositionFromGeolocation,
  requestFreshForegroundPosition,
  requestInitialForegroundPosition,
  type ForegroundJourneyTracker,
} from './foreground-geolocation';
import {
  acquireForegroundWakeLock,
  type ScreenWakeLockSource,
  type WakeLockLease,
} from './foreground-wake-lock';

export function deriveJourneySafety(
  active: boolean,
  authoritativePassive: boolean,
  recoveryLocked = false,
  confirmingPosition = false,
): JourneySafetyState {
  if (active && (authoritativePassive || recoveryLocked)) {
    return {
      mode: 'journey-passive',
      message: confirmingPosition
        ? 'Pause here while we confirm your position.'
        : recoveryLocked
          ? 'A foreground Journey is still open. Stop it to restore interactive controls.'
          : 'The server detected movement above the safety threshold. Discoveries queue without taps.',
    };
  }
  if (active) {
    return {
      mode: 'journey-recording',
      message: 'Foreground location samples are being verified in bounded batches.',
    };
  }
  return { mode: 'stationary' };
}

interface JourneySafetyValue {
  active: boolean;
  foregroundVisible: boolean;
  safety: JourneySafetyState;
  phase: 'idle' | 'starting' | 'recording' | 'stopping' | 'recovery';
  error?: string;
  capabilityNotice?: string;
  queuedDiscoveryCount: number;
  queuedDiscoveries: readonly DiscoveryMarkerView[];
  rewardDecision?: 'accepted' | 'held' | 'rejected';
  currentPosition?: JourneyMapPosition;
  activeRoute: readonly JourneyMapPosition[];
  journeyId?: string;
  startedAt?: string;
  movementMode?: JourneyAuthoritySnapshot['movementMode'];
  stationaryConfirmed: boolean;
  verificationRevision: number;
  beginJourney: () => Promise<void>;
  beginWeaveWalk: () => Promise<void>;
  cancelJourney: () => Promise<void>;
  stopJourney: () => Promise<void>;
  verifyCurrentPosition: (
    discoveryId: string,
    signal?: AbortSignal,
  ) => Promise<JourneyMapPosition | undefined>;
}

const JourneySafetyContext = createContext<JourneySafetyValue | null>(null);
const MOVING_JOURNEY_KEY = 'aztec-realms:known-moving-journey-id';

function knownMovingJourney(id: string | undefined): boolean {
  return Boolean(id && window.sessionStorage.getItem(MOVING_JOURNEY_KEY) === id);
}

function clearMovingJourney(id: string | undefined) {
  if (knownMovingJourney(id)) window.sessionStorage.removeItem(MOVING_JOURNEY_KEY);
}

function isPassiveVisualFixture(): boolean {
  return visualFixtureName() === 'passive';
}

function isExplicitScenarioAFixture(): boolean {
  if (typeof window === 'undefined') return false;
  const e2e = (window as Window & { __AZR_E2E__?: { deterministic?: boolean } }).__AZR_E2E__;
  return (
    e2e?.deterministic === true &&
    new URLSearchParams(window.location.search).get('fixture') === 'scenario-a'
  );
}

export function JourneySafetyProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [recoveryJourneyId, setRecoveryJourneyId] = useState(() => persistedJourneyId());
  const [foregroundVisible, setForegroundVisible] = useState(document.visibilityState !== 'hidden');
  const passiveFixture = isPassiveVisualFixture();
  const [active, setActive] = useState(Boolean(recoveryJourneyId) || passiveFixture);
  const [authority, setAuthority] = useState<JourneyAuthoritySnapshot | undefined>(() =>
    passiveFixture
      ? {
          id: 'visual-fixture-journey',
          nonce: 'visual-fixture-nonce',
          nextSequenceNo: 1,
          status: 'active',
          passiveMode: true,
          movementMode: 'transit',
          interactionLocked: true,
          stationaryConfirmed: false,
          fraudScore: 0,
          rewardDecision: 'accepted',
          queuedDiscoveryCount: 2,
          queuedDiscoveries: [],
        }
      : undefined,
  );
  const [phase, setPhase] = useState<JourneySafetyValue['phase']>(
    recoveryJourneyId ? 'recovery' : passiveFixture ? 'recording' : 'idle',
  );
  const [error, setError] = useState<string>();
  const [capabilityNotice, setCapabilityNotice] = useState<string>();
  const [activeRoute, setActiveRoute] = useState<readonly JourneyMapPosition[]>([]);
  const [verificationRevision, setVerificationRevision] = useState(0);
  const trackerRef = useRef<ForegroundJourneyTracker | null>(null);
  const wakeLockRef = useRef<WakeLockLease | null>(null);
  const epochRef = useRef(0);
  const mountedRef = useRef(true);
  const startingRef = useRef<Promise<void> | null>(null);
  const startingEpochRef = useRef(0);
  const closingRef = useRef<Promise<void> | null>(null);
  const journeyIdRef = useRef(recoveryJourneyId);
  const recoveryLocked =
    phase === 'recovery' || knownMovingJourney(authority?.id ?? recoveryJourneyId);

  const applyAuthority = useCallback(
    (next: JourneyAuthoritySnapshot) => {
      if (next.passiveMode || next.interactionLocked) {
        window.sessionStorage.setItem(MOVING_JOURNEY_KEY, next.id);
      } else if (next.stationaryConfirmed) {
        clearMovingJourney(next.id);
      }
      setAuthority(next);
      journeyIdRef.current = next.status === 'active' ? next.id : undefined;
      setVerificationRevision((current) => current + 1);
      setActive(next.status === 'active');
      setPhase(next.status === 'active' ? 'recording' : 'idle');
      setError(undefined);
      if (next.queuedDiscoveryCount > 0) {
        void queryClient.invalidateQueries({ queryKey: ['player', 'vertical-slice', 'v1'] });
      }
    },
    [queryClient],
  );

  const beginJourney = useCallback(
    (newWalk = false): Promise<void> => {
      if (startingRef.current && startingEpochRef.current === epochRef.current)
        return startingRef.current;
      const priorStart = startingRef.current;
      if (
        document.visibilityState === 'hidden' ||
        phase === 'stopping' ||
        (active && phase !== 'recovery' && !newWalk) ||
        (newWalk && (authority?.interactionLocked || authority?.passiveMode || recoveryLocked))
      )
        return Promise.resolve();
      const epoch = ++epochRef.current;
      startingEpochRef.current = epoch;
      const isCurrent = () =>
        mountedRef.current && epochRef.current === epoch && document.visibilityState !== 'hidden';
      const begin = async () => {
        setPhase('starting');
        setError(undefined);
        setCapabilityNotice(undefined);
        setActiveRoute([]);
        if (demoModeEnabled()) {
          setActive(true);
          setPhase('recording');
          return;
        }
        try {
          await priorStart;
          await closingRef.current?.catch(() => undefined);
          if (!isCurrent()) return;
          // A Weave needs a fresh walk after planning. Finish the existing walk normally;
          // never silently cancel its queued discoveries or bypass the safe-finish check.
          if (newWalk && journeyIdRef.current) {
            const previousId = journeyIdRef.current;
            await trackerRef.current?.stop();
            if (!isCurrent()) return;
            await finishForegroundJourney(previousId);
            if (!isCurrent()) return;
            trackerRef.current = null;
            journeyIdRef.current = undefined;
            setAuthority(undefined);
            setRecoveryJourneyId(undefined);
            clearMovingJourney(previousId);
            await queryClient.invalidateQueries({ queryKey: ['player', 'vertical-slice', 'v1'] });
            if (!isCurrent()) return;
          }
          const staleId = journeyIdRef.current ?? persistedJourneyId();
          if (staleId && !knownMovingJourney(staleId)) {
            await cancelForegroundJourney(staleId);
            journeyIdRef.current = undefined;
            setRecoveryJourneyId(undefined);
          }
          const fixtureMode = isExplicitScenarioAFixture();
          if (!fixtureMode && !navigator.geolocation) {
            throw new Error(
              'This browser has no GPS access. Use Daily Codex; no Journey was started.',
            );
          }
          const initialPosition = fixtureMode
            ? undefined
            : await requestInitialForegroundPosition(navigator.geolocation);
          if (!isCurrent()) return;
          const journey = await startForegroundJourney(fixtureMode ? 'fixture' : 'foreground');
          journeyIdRef.current = journey.id;
          if (!isCurrent()) {
            if (journey.passiveMode || journey.interactionLocked) {
              window.sessionStorage.setItem(MOVING_JOURNEY_KEY, journey.id);
            }
            if (knownMovingJourney(journey.id)) {
              if (mountedRef.current) {
                setRecoveryJourneyId(journey.id);
                setActive(true);
                setPhase('recovery');
              }
              return;
            }
            await cancelForegroundJourney(journey.id, { keepalive: true });
            journeyIdRef.current = undefined;
            return;
          }
          applyAuthority(journey);
          if (fixtureMode) return;
          const tracker = createForegroundJourneyTracker({
            geolocation: navigator.geolocation,
            initialPosition,
            journey,
            submit: submitForegroundJourneySamples,
            onAuthority: (next) => {
              if (isCurrent()) applyAuthority(next);
            },
            onError: (message) => {
              if (isCurrent()) setError(message);
            },
            onRecoveryRequired: (message) => {
              if (!isCurrent()) return;
              setAuthority(undefined);
              setRecoveryJourneyId(journey.id);
              setActive(true);
              setPhase('recovery');
              setError(message);
            },
            onPosition: (position) => {
              if (isCurrent())
                setActiveRoute((current) => appendJourneyPresentationPosition(current, position));
            },
            document,
          });
          trackerRef.current = tracker;
          tracker.start();
        } catch (caught) {
          if (!mountedRef.current) return;
          const unresolved = journeyIdRef.current ?? persistedJourneyId();
          setRecoveryJourneyId(unresolved);
          setActive(Boolean(unresolved));
          setPhase(unresolved ? 'recovery' : 'idle');
          setError(caught instanceof Error ? caught.message : 'The Journey could not start.');
        }
      };
      const pending = begin().finally(() => {
        if (startingRef.current === pending) startingRef.current = null;
      });
      startingRef.current = pending;
      return pending;
    },
    [active, applyAuthority, authority, phase, queryClient, recoveryLocked],
  );
  const beginWeaveWalk = useCallback(() => beginJourney(true), [beginJourney]);

  const cancelJourney = useCallback(async () => {
    if (!active || phase === 'stopping' || startingRef.current) return;
    setPhase('stopping');
    epochRef.current += 1;
    setError(undefined);
    const journeyId = authority?.id ?? recoveryJourneyId;
    try {
      await trackerRef.current?.discard();
      trackerRef.current = null;
      if (journeyId && !passiveFixture && !demoModeEnabled()) {
        await cancelForegroundJourney(journeyId);
      }
      clearMovingJourney(journeyId);
      setAuthority(undefined);
      journeyIdRef.current = undefined;
      setRecoveryJourneyId(undefined);
      setActiveRoute([]);
      setActive(false);
      setPhase('idle');
      setCapabilityNotice('Journey cancelled. No completion reward was granted.');
      await queryClient.invalidateQueries({ queryKey: ['player', 'vertical-slice', 'v1'] });
    } catch (caught) {
      setPhase(authority ? 'recording' : 'recovery');
      setError(caught instanceof Error ? caught.message : 'The Journey could not be cancelled.');
    }
  }, [active, authority, passiveFixture, phase, queryClient, recoveryJourneyId]);

  const stopJourney = useCallback(async () => {
    if (!active || phase === 'stopping' || startingRef.current) return;
    const epoch = ++epochRef.current;
    const isCurrent = () => mountedRef.current && epochRef.current === epoch;
    setPhase('stopping');
    setError(undefined);
    if (passiveFixture || demoModeEnabled()) {
      await trackerRef.current?.stop();
      if (!isCurrent()) return;
      trackerRef.current = null;
      setAuthority(undefined);
      journeyIdRef.current = undefined;
      setRecoveryJourneyId(undefined);
      setActiveRoute([]);
      setActive(false);
      setPhase('idle');
      setCapabilityNotice(undefined);
      return;
    }
    const journeyId = authority?.id ?? recoveryJourneyId;
    try {
      await trackerRef.current?.stop();
      if (!isCurrent()) return;
      trackerRef.current = null;
      if (journeyId) await finishForegroundJourney(journeyId);
      if (!isCurrent()) return;
      clearMovingJourney(journeyId);
      setAuthority(undefined);
      journeyIdRef.current = undefined;
      setRecoveryJourneyId(undefined);
      setActiveRoute([]);
      setActive(false);
      setPhase('idle');
      setCapabilityNotice(undefined);
      await queryClient.invalidateQueries({ queryKey: ['player', 'vertical-slice', 'v1'] });
    } catch (caught) {
      if (!isCurrent()) return;
      setAuthority(undefined);
      setRecoveryJourneyId(journeyId);
      setActive(Boolean(journeyId));
      setPhase(journeyId ? 'recovery' : 'idle');
      setError(caught instanceof Error ? caught.message : 'The Journey could not finish.');
    }
  }, [active, authority, passiveFixture, phase, queryClient, recoveryJourneyId]);

  const verifyCurrentPosition = useCallback(
    async (discoveryId: string, signal?: AbortSignal) => {
      if (signal?.aborted) return undefined;
      if (!active || phase !== 'recording') {
        setError('Start a foreground Journey before checking this stop.');
        return undefined;
      }
      const tracker = trackerRef.current;
      if (!tracker) {
        setError(
          'Live GPS is not ready yet. Keep Explore visible and wait for a fresh location fix.',
        );
        return undefined;
      }
      setError(undefined);
      const epoch = epochRef.current;
      const isCurrent = () =>
        mountedRef.current &&
        epochRef.current === epoch &&
        trackerRef.current === tracker &&
        !signal?.aborted &&
        document.visibilityState !== 'hidden';
      try {
        const freshPosition = await requestFreshForegroundPosition(navigator.geolocation, signal);
        if (!isCurrent()) return undefined;
        const submitted = await tracker.verify(freshPosition, discoveryId, signal);
        return submitted && isCurrent() ? mapPositionFromGeolocation(freshPosition) : undefined;
      } catch (caught) {
        if (isCurrent())
          setError(caught instanceof Error ? caught.message : 'This stop could not be verified.');
        return undefined;
      }
    },
    [active, phase],
  );

  useEffect(() => {
    mountedRef.current = true;
    function closePersistedJourney(id: string | undefined) {
      if (
        !id ||
        knownMovingJourney(id) ||
        demoModeEnabled() ||
        isPassiveVisualFixture() ||
        closingRef.current
      )
        return;
      const closing = cancelForegroundJourney(id, { keepalive: true })
        .then(() => {
          if (journeyIdRef.current === id) journeyIdRef.current = undefined;
          if (mountedRef.current) {
            setRecoveryJourneyId(undefined);
            setAuthority(undefined);
            setActive(false);
            setPhase('idle');
          }
        })
        .catch((caught: unknown) => {
          if (mountedRef.current) {
            setRecoveryJourneyId(id);
            setAuthority(undefined);
            setActive(true);
            setPhase('recovery');
            setError(
              caught instanceof Error
                ? caught.message
                : 'Journey cleanup will retry when you return.',
            );
          }
        })
        .finally(() => {
          if (closingRef.current === closing) closingRef.current = null;
        });
      closingRef.current = closing;
    }
    function suspend() {
      epochRef.current += 1;
      void trackerRef.current?.discard();
      trackerRef.current = null;
      const lease = wakeLockRef.current;
      wakeLockRef.current = null;
      void lease?.release();
      const id = journeyIdRef.current ?? persistedJourneyId();
      if (mountedRef.current) {
        setActiveRoute([]);
        setForegroundVisible(false);
        setPhase(knownMovingJourney(id) ? 'recovery' : id ? 'stopping' : 'idle');
      }
      closePersistedJourney(id);
    }
    function visibilityChanged() {
      if (document.visibilityState === 'hidden') suspend();
      else setForegroundVisible(true);
    }
    function pageShown() {
      setForegroundVisible(document.visibilityState !== 'hidden');
    }
    document.addEventListener('visibilitychange', visibilityChanged);
    window.addEventListener('pagehide', suspend);
    window.addEventListener('pageshow', pageShown);
    // A previous document may have closed before its cancellation reached the server.
    // Reconcile on every route; only Explore may choose to begin fresh GPS tracking.
    closePersistedJourney(journeyIdRef.current ?? persistedJourneyId());
    return () => {
      mountedRef.current = false;
      suspend();
      document.removeEventListener('visibilitychange', visibilityChanged);
      window.removeEventListener('pagehide', suspend);
      window.removeEventListener('pageshow', pageShown);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function releaseWakeLock() {
      const lease = wakeLockRef.current;
      wakeLockRef.current = null;
      await lease?.release();
    }

    async function acquireIfVisible() {
      if (!active || document.visibilityState === 'hidden' || wakeLockRef.current?.acquired) return;
      const source = (navigator as Navigator & { wakeLock?: ScreenWakeLockSource }).wakeLock;
      const lease = await acquireForegroundWakeLock(source);
      if (cancelled || !active || document.hidden) {
        await lease.release();
        return;
      }
      wakeLockRef.current = lease;
      setCapabilityNotice(
        lease.acquired
          ? undefined
          : lease.supported
            ? 'The screen wake lock was unavailable. Keep this browser visible for Journey tracking.'
            : 'This browser has no screen wake lock. Keep it visible for Journey tracking.',
      );
    }

    function visibilityChanged() {
      if (document.visibilityState === 'hidden') void releaseWakeLock();
      else void acquireIfVisible();
    }

    if (active) {
      void acquireIfVisible();
      document.addEventListener('visibilitychange', visibilityChanged);
    } else {
      void releaseWakeLock();
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', visibilityChanged);
      void releaseWakeLock();
    };
  }, [active]);

  const safety = useMemo(
    () =>
      deriveJourneySafety(
        active,
        authority?.passiveMode ?? false,
        recoveryLocked,
        phase !== 'recovery' &&
          Boolean(authority?.interactionLocked) &&
          authority?.movementMode !== 'transit' &&
          !authority?.stationaryConfirmed,
      ),
    [active, authority, phase, recoveryLocked],
  );
  const value = useMemo<JourneySafetyValue>(
    () => ({
      active,
      foregroundVisible,
      safety,
      phase,
      error,
      capabilityNotice,
      queuedDiscoveryCount: authority?.queuedDiscoveryCount ?? 0,
      queuedDiscoveries: authority?.queuedDiscoveries ?? [],
      rewardDecision: authority?.rewardDecision,
      currentPosition: activeRoute.at(-1),
      activeRoute,
      journeyId: authority?.id ?? recoveryJourneyId ?? undefined,
      startedAt: authority?.startedAt,
      movementMode: authority?.movementMode,
      stationaryConfirmed: authority?.stationaryConfirmed ?? false,
      verificationRevision,
      beginJourney,
      beginWeaveWalk,
      cancelJourney,
      stopJourney,
      verifyCurrentPosition,
    }),
    [
      active,
      foregroundVisible,
      activeRoute,
      authority,
      beginJourney,
      beginWeaveWalk,
      cancelJourney,
      capabilityNotice,
      error,
      phase,
      recoveryJourneyId,
      safety,
      stopJourney,
      verificationRevision,
      verifyCurrentPosition,
    ],
  );

  return <JourneySafetyContext.Provider value={value}>{children}</JourneySafetyContext.Provider>;
}

export function useJourneySafety(): JourneySafetyValue {
  const value = useContext(JourneySafetyContext);
  if (!value) throw new Error('useJourneySafety must be used inside JourneySafetyProvider');
  return value;
}
